import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Phone, BookOpen, Calendar, User } from 'lucide-react';
import Image from '../../components/AppImage';

const Blogs = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const blogPosts = [
  {
    id: 1,
    title: 'Top 10 Tourist Destinations Near Ludhiana',
    excerpt: 'Discover the best places to visit near Ludhiana with our comfortable taxi services. From historical sites to natural beauty, explore Punjab like never before.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_154d41be9-1768617096523.png",
    imageAlt: 'Beautiful tourist destination in Punjab with historical monuments',
    author: 'Harsh Nagpal',
    date: '2024-01-10',
    category: 'Travel Guide'
  },
  {
    id: 2,
    title: 'How to Choose the Right Cab for Your Journey',
    excerpt: 'Learn about different vehicle types and how to select the perfect cab based on your travel needs, group size, and budget.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a30b74c-1768617097438.png",
    imageAlt: 'Different types of cabs and vehicles for travel',
    author: 'Harsh Nagpal',
    date: '2024-01-08',
    category: 'Travel Tips'
  },
  {
    id: 3,
    title: 'Ludhiana to Delhi: Complete Travel Guide',
    excerpt: 'Everything you need to know about traveling from Ludhiana to Delhi by taxi - routes, timings, fares, and travel tips.',
    image: "https://images.unsplash.com/photo-1725162758948-6fc8c37b08da",
    imageAlt: 'Highway route from Ludhiana to Delhi',
    author: 'Harsh Nagpal',
    date: '2024-01-05',
    category: 'Route Guide'
  },
  {
    id: 4,
    title: 'Benefits of Pre-Booking Your Taxi',
    excerpt: 'Discover why pre-booking your taxi can save you time, money, and ensure a stress-free travel experience.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c94d75f-1764791136624.png",
    imageAlt: 'Person booking taxi online on smartphone',
    author: 'Harsh Nagpal',
    date: '2024-01-03',
    category: 'Travel Tips'
  },
  {
    id: 5,
    title: 'Chandigarh to Haridwar: Spiritual Journey Guide',
    excerpt: 'Plan your spiritual journey from Chandigarh to Haridwar with our comprehensive guide covering routes, stops, and travel essentials.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_150bf8299-1768617097204.png",
    imageAlt: 'Scenic route to Haridwar with mountains',
    author: 'Harsh Nagpal',
    date: '2024-01-01',
    category: 'Route Guide'
  },
  {
    id: 6,
    title: 'Corporate Travel Solutions: Why Choose Advait Tour and Travels',
    excerpt: 'Explore our corporate travel solutions designed for businesses, offering reliability, professionalism, and cost-effective transportation.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_126693a8f-1768327187969.png",
    imageAlt: 'Business professionals traveling in premium cab',
    author: 'Harsh Nagpal',
    date: '2023-12-28',
    category: 'Corporate Travel'
  }];


  return (
    <>
      <SEO
        title="Blogs - Travel Tips & Guides | Advait Tour and Travels"
        description="Read travel tips, destination guides, and taxi service insights from Advait Tour and Travels. Stay updated with the latest travel information for Ludhiana, Delhi, Chandigarh, and more."
        keywords="travel blogs, taxi tips, destination guides, ludhiana travel, delhi travel, chandigarh travel, advait tour and travels blog, travel guide punjab"
        canonicalUrl={`${window.location?.origin}/blogs`}
        ogTitle="Blogs - Travel Tips & Guides | Advait Tour and Travels"
        ogDescription="Read travel tips, destination guides, and taxi service insights from Advait Tour and Travels."
        ogUrl={`${window.location?.origin}/blogs`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Advait Tour and Travels Blog",
          "description": "Travel tips, destination guides, and taxi service insights"
        }} />

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="py-20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-20 mb-12">
            <div className="container mx-auto px-4 text-center">
              <BookOpen className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Advait Tour and Travels Blog
              </h1>
              <p className="text-xl text-white max-w-3xl mx-auto">
                Travel Tips, Destination Guides & Insights
              </p>
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div className="container mx-auto px-4 mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {blogPosts?.map((post) =>
              <div
                key={post?.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer">

                  <div className="aspect-video overflow-hidden">
                    <Image
                    src={post?.image}
                    alt={post?.imageAlt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />

                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-600 text-xs font-semibold rounded-full">
                        {post?.category}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-3 hover:text-blue-600 transition-colors">
                      {post?.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post?.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-200">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{post?.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(post?.date)?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="container mx-auto px-4 mb-16">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Stay Updated with Travel Tips
              </h2>
              <p className="text-xl text-white mb-6">
                Subscribe to our newsletter for the latest travel guides and exclusive offers
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white" />

                <button className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-colors duration-300">
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 py-12">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                We're here for you 24/7!
              </h2>
              <p className="text-xl text-white mb-6">
                Give us a call anytime
              </p>
              <a
                href="tel:+917998666660"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 font-bold text-2xl rounded-full hover:bg-gray-100 transition-colors duration-300">

                <Phone className="w-6 h-6" />
                +91-79986-66660
              </a>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default Blogs;